import { Grid, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ProductPassportButton } from "../../components/buttons/ProductPassportButton";
import { PriceDisplay } from "../../components/common/PriceDisplay";
import {
    BOOLEAN_FIELD_TYPE,
    NUMERIC_RANGE_FIELD_TYPE,
    SELECT_FIELD_TYPE,
    TEXT_FIELD_TYPE,
    AUTOCOMPLETE_FIELD_TYPE
} from "../../helpers/constants";
import {
    useInvestments,
    useProductStatuses,
    useProductTypes,
    useStyleSelectList
} from "../../services/productsService";
import { getCalculatedPricingForProduct, useLocalizedProperty } from "../../utils";
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { ProductStatusText } from "./ProductStatusText";

import { toast } from "react-hot-toast";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import { documentsApi } from "../../services/documentsService";
import { convertFlatListFromBackendToTranslatedOptionObject } from "../../helpers/methods";

export const PRODUCTS_TABLE_NAME = "products_table";

export const useProductsTableFieldsConfig = () => {
    const { t } = useTranslation();

    const getLocalizedProperty = useLocalizedProperty();

    const boolToString = (value) => (value ? t("true") : t("false"));

    const { data: types } = useProductTypes(new URLSearchParams(), {
        select: (data) =>
            data.map((type) => ({
                id: type.id,
                label: getLocalizedProperty(type, "name"),
            })),
    });

    const { data: statuses } = useProductStatuses(new URLSearchParams(), {
        select: (data) =>
            data.results.map((status) => ({
                value: status.status_name_eng,
                label: t(`products.status.${status.status_name_eng}`),
            })),
    });

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });


    const { data: styles } = useStyleSelectList(new URLSearchParams(), {
        select: (data) =>
            data.map((style) => ({
                value: style.id,
                label: style.investment_style,
            })),
    });

    const axiosPrivate = useAxiosPrivate();
    const onPreview = async (e, docId) => {
        e.stopPropagation()
        try {
            await documentsApi.handlePreviewDocumentFromLink(axiosPrivate, docId);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_preview"));
        }
    };

    return [
        {
            name: "product_number",
            getValueCallback: (rowData) => (
                <Grid
                    item
                    sx={{
                        minHeight: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {rowData.product_number}
                </Grid>
            ),
            label: t("products.columns.number"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            width: 120,
            sortable: true,
        },
        {
            name: "name_full",
            getValueCallback: (rowData) => rowData.name_full,
            label: t("products.columns.nameFull"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            sortable: true,
        },
        {
            name: "type_id",
            other_export_field_name: "type",
            getValueCallback: (rowData) =>
                rowData.type ? getLocalizedProperty(rowData.type, "name") : "",
            label: t("products.columns.productType"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            minWidth: 120,
            width: 120,
            fieldConfig: {
                optionLabelKey: "label",
                options: types || [],
                multiple: true,
                isObjectOption: true,
                disableClearable: false
            },
        },
        {
            name: "investment",
            getValueCallback: (rowData) => rowData.investment.investment_name_sales,
            label: t("products.columns.investmentName"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                optionKey: "value",
                optionLabel: "label",
                options: investments,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: investments,
            },
            minWidth: 120,
            width: 120,
        },
        {
            name: "style",
            getValueCallback: (rowData) => rowData.style?.style_name,
            label: t("products.columns.style"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                optionKey: "value",
                optionLabel: "label",
                options: styles,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: styles,
            },
            sortable: false,
            minWidth: 120,
            width: 120,
        },
        {
            name: "hotel_note",
            getValueCallback: (rowData) => rowData.hotel_note,
            label: t("products.columns.productHotelNote"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
        },
        {
            name: "floor",
            getValueCallback: (rowData) => rowData.floor,
            label: t("products.columns.floor"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "num_rooms",
            getValueCallback: (rowData) => rowData.num_rooms,
            label: t("products.columns.nRooms"),
            filterType: NUMERIC_RANGE_FIELD_TYPE, // Disabled for now
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "footage",
            getValueCallback: (rowData) => rowData.footage,
            label: t("products.columns.footage"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "status",
            getValueCallback: (rowData) => <ProductStatusText status={rowData.status} />,
            label: t("products.columns.productStatus"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                optionKey: "value",
                optionLabel: "label",
                options: statuses,
            }),
            minWidth: 120,
            width: 120,
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: statuses,
            },
        },
        {
            name: "project_number",
            getValueCallback: (rowData) => rowData.product_number,
            label: t("products.columns.projectNumber"),
            minWidth: 120,
            width: 120,
        },
        // {
        //     name: "side_n",
        //     getValueCallback: (rowData) => boolToString(rowData.orientation.north),
        //     label: t("products.columns.direction_north"),
        //     filterType: BOOLEAN_FIELD_TYPE,
        //     getFilterFieldConfigCallback: (filteringData) => undefined,
        //     minWidth: 120,
        //     width: 120,
        // },
        // {
        //     name: "side_s",
        //     getValueCallback: (rowData) => boolToString(rowData.orientation.south),
        //     label: t("products.columns.direction_south"),
        //     filterType: BOOLEAN_FIELD_TYPE,
        //     getFilterFieldConfigCallback: (filteringData) => undefined,
        //     minWidth: 120,
        //     width: 120,
        // },
        // {
        //     name: "side_e",
        //     getValueCallback: (rowData) => boolToString(rowData.orientation.east),
        //     label: t("products.columns.direction_east"),
        //     filterType: BOOLEAN_FIELD_TYPE,
        //     getFilterFieldConfigCallback: (filteringData) => undefined,
        //     minWidth: 120,
        //     width: 120,
        // },
        // {
        //     name: "side_w",
        //     getValueCallback: (rowData) => boolToString(rowData.orientation.west),
        //     label: t("products.columns.direction_west"),
        //     filterType: BOOLEAN_FIELD_TYPE,
        //     getFilterFieldConfigCallback: (filteringData) => undefined,
        //     minWidth: 120,
        //     width: 120,
        // },
        {
            name: "orientation",
            getValueCallback: (rowData) =>
                rowData.flat_orientation.map(orientation=> t(`products.columns.${orientation}`)).join(", "),
            label: t("products.columns.orientation"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            fieldConfig: {
                multiple : true,
                isObjectOption: true,
                disableClearable: false,
                options: convertFlatListFromBackendToTranslatedOptionObject(["north", "south", "east", "west"], "id", "name", "products.columns"),
            },
            sortable: false,
            minWidth: 120,
            width: 120,
        },
        {
            name: "listed_price_gross",
            getValueCallback: (rowData) => {
                const pricing = getCalculatedPricingForProduct(rowData);
                return <PriceDisplay price={pricing.listed.gross} />;
            },
            label: t("products.columns.priceGross"),
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            width: 120,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "listed_price_net",
            getValueCallback: (rowData) => {
                const pricing = getCalculatedPricingForProduct(rowData);
                return <PriceDisplay price={pricing.listed.net} />;
            },
            label: t("products.columns.priceNet"),
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "listed_price_per_sqm_gross",
            getValueCallback: (rowData) => {
                const pricing = getCalculatedPricingForProduct(rowData);
                return <PriceDisplay price={pricing.listed.gross_per_sqm} perSquareMeter />;
            },
            label: t("products.columns.priceGrossM2"),
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            width: 120,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "listed_price_per_sqm_net",
            getValueCallback: (rowData) => {
                const pricing = getCalculatedPricingForProduct(rowData);
                return <PriceDisplay price={pricing.listed.net_per_sqm} perSquareMeter />;
            },
            label: t("products.columns.priceNetM2"),
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            width: 120,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "minimum_price_gross",
            getValueCallback: (rowData) => {
                const pricing = getCalculatedPricingForProduct(rowData);
                return <PriceDisplay price={pricing.minSale.gross} />;
            },
            label: t("products.columns.priceMinimumGross"),
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            width: 120,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "minimum_price_per_sqm_gross",
            getValueCallback: (rowData) => {
                const pricing = getCalculatedPricingForProduct(rowData);
                return <PriceDisplay price={pricing.minSale.gross_per_sqm} perSquareMeter />;
            },
            label: t("products.columns.priceMinimumGrossM2"),
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            width: 120,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            sortable: true,
        },
        {
            name: "note",
            getValueCallback: (rowData) => rowData.note,
            label: t("products.columns.productNote"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
        },
        {
            name: "tag",
            getValueCallback: (rowData) => rowData.tags.map((tag) => tag.tag_name).join(", "),
            label: t("products.columns.tags"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "card_pdf",
            getValueCallback: (rowData) => (
                rowData.card_pdf?.id 
                ? <IconButton onClick={(e)=>onPreview(e, rowData.card_pdf.id)}>
                    <DocumentScannerIcon color={"primary"} /> 
                  </IconButton>
                : null),
            label: t("products.columns.card_pdf"),
            filterType: null,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            minWidth: 120,
            sortable: false,
            excludedFromExport: true,
        },
        {
            name: "link_button", // This cannot be empty because it breaks dnd in table customization
            getValueCallback: (rowData) => <ProductPassportButton productId={rowData.id} />,
            label: t("products.columns.manage"),
            minWidth: 120,
            width: 120,
            allowChangeVisibility: false,
            excludedFromExport: true,
        },
    ];
};
